import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge } from '../../ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '../../ui/tooltip';
import { Truck, ExternalLink } from 'lucide-react';
import { formatAbsoluteTime } from '../../../utils/inventoryHelpers';

/**
 * POSourceChip Component
 * Displays a clickable chip for Purchase Order source with navigation
 *
 * @param {Object} props
 * @param {Object} props.source - Source origin object with PO information
 * @param {string} props.source.source_id - PO MongoDB ObjectId
 * @param {string} props.source.source_reference - PO reference number (e.g., "PO-2024-1234")
 * @param {string} props.source.supplier_name - Supplier name
 * @param {number} props.source.quantity - Quantity from this PO
 * @param {string} props.source.received_date - Date received
 * @param {'small'|'medium'} props.size - Chip size (default: 'small')
 * @param {boolean} props.showQuantity - Whether to show quantity (default: true)
 * @param {boolean} props.showTooltip - Whether to show detailed tooltip (default: true)
 * @param {Function} props.onClick - Optional custom click handler
 */
function POSourceChip({
  source,
  size = 'small',
  showQuantity = true,
  showTooltip = true,
  onClick
}) {
  const navigate = useNavigate();

  if (!source || !source.source_id) {
    return null;
  }

  const handleClick = (e) => {
    e.stopPropagation(); // Prevent event bubbling

    if (onClick) {
      onClick(source);
    } else if (source.source_id) {
      navigate(`/purchases/${source.source_id}/view`);
    }
  };

  // Format label
  const label = showQuantity && source.quantity
    ? `${source.source_reference || 'PO'} (${source.quantity}支)`
    : (source.source_reference || 'PO');

  // Tooltip content
  const tooltipContent = showTooltip ? (
    <div className="space-y-1">
      <p className="font-medium text-sm">
        {source.source_reference || '采购订单'}
      </p>
      {source.supplier_name && (
        <p className="text-xs">供应商: {source.supplier_name}</p>
      )}
      {source.quantity && (
        <p className="text-xs">数量: {source.quantity}支</p>
      )}
      {source.received_date && (
        <p className="text-xs">
          收货日期: {formatAbsoluteTime(source.received_date, false)}
        </p>
      )}
      <p className="text-xs text-primary mt-1">
        点击查看采购订单详情
      </p>
    </div>
  ) : null;

  const chip = (
    <button
      onClick={handleClick}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-gray-200 bg-white
        hover:bg-muted hover:-translate-y-px hover:shadow-sm transition-all duration-200 cursor-pointer
        ${size === 'small' ? 'text-xs h-5' : 'text-sm h-7'}`}
    >
      <Truck className={size === 'small' ? 'h-3 w-3' : 'h-4 w-4'} />
      <span>{label}</span>
      <ExternalLink className={size === 'small' ? 'h-2.5 w-2.5' : 'h-3 w-3'} />
    </button>
  );

  return showTooltip ? (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>{chip}</TooltipTrigger>
        <TooltipContent>{tooltipContent}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  ) : chip;
}

/**
 * POSourceList Component
 * Displays a list of PO source chips
 *
 * @param {Object} props
 * @param {Array} props.sources - Array of source origin objects
 * @param {number} props.maxVisible - Maximum number of chips to show (default: 2)
 * @param {'small'|'medium'} props.size - Chip size (default: 'small')
 * @param {boolean} props.showQuantity - Whether to show quantities (default: true)
 */
export function POSourceList({
  sources = [],
  maxVisible = 2,
  size = 'small',
  showQuantity = true
}) {
  if (!sources || sources.length === 0) {
    return (
      <span className="text-xs text-muted-foreground">暂无来源信息</span>
    );
  }

  const visibleSources = sources.slice(0, maxVisible);
  const hiddenCount = sources.length - maxVisible;

  return (
    <div className="flex flex-wrap gap-1 items-center">
      {visibleSources.map((source, index) => (
        <POSourceChip
          key={index}
          source={source}
          size={size}
          showQuantity={showQuantity}
        />
      ))}
      {hiddenCount > 0 && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge
                variant="outline"
                className={`cursor-help ${size === 'small' ? 'h-5 text-xs' : 'h-7 text-sm'}`}
              >
                +{hiddenCount} 更多
              </Badge>
            </TooltipTrigger>
            <TooltipContent>
              <div>
                <p className="font-medium text-xs mb-1">其他来源:</p>
                {sources.slice(maxVisible).map((source, index) => (
                  <p key={index} className="text-xs">
                    • {source.source_reference} ({source.quantity}支)
                  </p>
                ))}
              </div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </div>
  );
}

export default POSourceChip;
